import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { useMap } from "react-map-gl/maplibre";
import { useTheme } from "@/components/theme-provider";
import maplibregl from "maplibre-gl";
import { LocateFixed } from "lucide-react";

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 10000,
};

const createMarkerElement = () => {
  const el = document.createElement('div');
  Object.assign(el.style, {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#4285F4',
    border: '3px solid white',
    boxShadow: '0 0 6px rgba(0,0,0,0.3)',
  });
  return el;
};

export const MyLocationControl: React.FC = () => {
  const { current: map } = useMap();
  const { theme } = useTheme();
  const [isTracking, setIsTracking] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);

  const cleanup = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    markerRef.current?.remove();
    markerRef.current = null;
    setIsTracking(false);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const updateLocation = useCallback((latitude: number, longitude: number, flyTo = false) => {
    if (!map) return;

    if (flyTo) {
      map.flyTo({ center: [longitude, latitude], zoom: 15, duration: 1000 });
    } else {
      map.easeTo({ center: [longitude, latitude], duration: 1000 });
    }

    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ element: createMarkerElement() })
        .setLngLat([longitude, latitude])
        .addTo(map.getMap());
    } else {
      markerRef.current.setLngLat([longitude, latitude]);
    }
  }, [map]);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation || !map) return;

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        updateLocation(latitude, longitude, true);
        setIsLocating(false);
        setIsTracking(true);

        watchIdRef.current = navigator.geolocation.watchPosition(
          (pos) => updateLocation(pos.coords.latitude, pos.coords.longitude),
          cleanup,
          GEO_OPTIONS
        );
      },
      () => setIsLocating(false),
      GEO_OPTIONS
    );
  }, [map, updateLocation, cleanup]);

  const handleClick = () => {
    if (isTracking || isLocating) {
      cleanup();
    } else {
      startTracking();
    }
  };

  const getTitle = () => {
    if (isLocating) return 'Locating...';
    if (isTracking) return 'Stop tracking';
    return 'Find my location';
  };

  return (
    <Card
      className={`border transition-all duration-200 w-12 p-0 py-2 ${
        theme === "dark"
          ? "bg-gray-900/95 text-white border-gray-700"
          : "bg-white/95 text-black border-gray-200"
      }`}
    >
      <div
        className={`flex items-center justify-center p-0 cursor-pointer hover:bg-gray-800/20 dark:hover:bg-gray-100/20 transition-colors ${
          isLocating ? 'animate-pulse' : ''
        } ${isTracking ? 'text-blue-600 dark:text-blue-400' : ''}`}
        onClick={handleClick}
        title={getTitle()}
      >
        <LocateFixed className="h-5 w-5" />
      </div>
    </Card>
  );
};


